
import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Table, Tag, Space, Button, Dropdown, Input, Select, Modal, message } from 'antd';
import { SearchOutlined, MoreOutlined, MailOutlined, MessageOutlined, UserOutlined, LockOutlined, DeleteOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import useGroupsStore from '../../stores/useGroupsStore';
import moment from 'moment';
import UserAvatar from '../common/UserAvatar';
import AddContactsModal from './AddContactsModal';
import { addGroupContactsV3 } from '../../reducers/group/resources';

const GroupList = ({ history }) => {
    const { groups, loading, filters, setFilter, searchQuery, setSearchQuery, deleteGroup, archiveGroup, getFilteredGroups, duplicateGroup, exportGroup, fetchGroups } = useGroupsStore();

    // Use filtered groups
    const filteredGroups = getFilteredGroups();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Add Contacts Modal State
    const [addContactsModalOpen, setAddContactsModalOpen] = useState(false);
    const [selectedGroupForAdd, setSelectedGroupForAdd] = useState(null);
    const [addingContacts, setAddingContacts] = useState(false);

    const handleDeleteClick = (group) => {
        setSelectedGroup(group);
        setDeleteModalOpen(true);
    };

    const handleArchive = () => {
        if (selectedGroup) {
            archiveGroup(selectedGroup.id);
            setDeleteModalOpen(false);
            setSelectedGroup(null);
        }
    };

    const handleDelete = () => {
        if (selectedGroup) {
            deleteGroup(selectedGroup.id);
            setDeleteModalOpen(false);
            setSelectedGroup(null);
        }
    };

    const handleAddContactsTrigger = (group) => {
        setSelectedGroupForAdd(group);
        setAddContactsModalOpen(true);
    };

    const handleAddContactsToGroup = async (selectedObjects) => {
        if (!selectedGroupForAdd) return;
        setAddingContacts(true);
        try {
            const ids = selectedObjects.map(c => c.id);
            await addGroupContactsV3(selectedGroupForAdd.id, ids);
            message.success('Contacts added successfully');
            setAddContactsModalOpen(false);
            setSelectedGroupForAdd(null);
            fetchGroups(); // Refresh list to update counts
        } catch (error) {
            console.error(error);
            message.error('Failed to add contacts');
        } finally {
            setAddingContacts(false);
        }
    };

    // Mock data enrichment enrichment is now handled in store (or at least filtering is)
    // But store returns full objects. GroupList table expects data.
    const data = filteredGroups.map(g => ({
        key: g.id,
        ...g
    }));

    const columns = [

        {
            title: 'Group',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space direction="vertical" size={2}>
                    <Space size={4}>
                        <a
                            onClick={() => history.push(`/groups/${record.id}`)}
                            className="gh-table-link"
                        >
                            {text}
                        </a>
                        {record.is_archived && (
                            <Tag color="default" style={{ fontSize: 10, lineHeight: '16px' }}>
                                Archived
                            </Tag>
                        )}
                        {record.privacy === 'private' && (
                            <Tag 
                                icon={<LockOutlined />} 
                                color="warning" 
                                style={{ 
                                    fontSize: 10, 
                                    padding: '0 4px', 
                                    lineHeight: '16px', 
                                    margin: 0,
                                    borderColor: '#faad14', // Darker outline matching text-ish
                                    color: '#faad14',
                                    backgroundColor: '#fffbe6' 
                                }}
                            >
                                Your Private Group
                            </Tag>
                        )}
                    </Space>
                    <Space size={8} align="center">
                        {/* {record.purpose && (
                            <div style={{ 
                                display: 'inline-block',
                                backgroundColor: '#fff', 
                                border: '1px solid #d9d9d9', 
                                fontSize: '11px', 
                                borderRadius: '12px', 
                                padding: '0 8px', 
                                lineHeight: '18px',
                                color: '#595959',
                                fontWeight: 400
                            }}>
                                {record.purpose}
                            </div>
                        )} */}
                    
                    </Space>
                </Space>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: 'Contacts',
            dataIndex: 'contact_count',
            key: 'contacts',
            render: (count) => (
                <span>
                    {count || 0}
                    {/* Growth indicator could go here */}
                </span>
            ),
            sorter: (a, b) => (a.contact_count || 0) - (b.contact_count || 0)
        },
        {
            title: 'Campaigns',
            dataIndex: 'campaign_count',
            key: 'campaigns',
            render: (count) => <span>{count || 0}</span>,
            sorter: (a, b) => (a.campaign_count || 0) - (b.campaign_count || 0)
        },
        {
            title: 'Last Messaged',
            dataIndex: 'last_schedule',
            key: 'last_schedule',
            render: (date, record) => (
                <Space>
                    {record.channel === 'SMS' && <MessageOutlined style={{ color: '#4CAF50' }} />}
                    {record.channel === 'Email' && <MailOutlined style={{ color: '#faad14' }} />}
                    {date ? moment(date).format('MMM DD, YYYY') : 'Never'}
                </Space>
            ),
            sorter: (a, b) => {
                const dateA = a.last_schedule ? new Date(a.last_schedule).getTime() : 0;
                const dateB = b.last_schedule ? new Date(b.last_schedule).getTime() : 0;
                return dateA - dateB;
            }
        },
        {
            title: 'Created By',
            key: 'created_by',
            render: (_, record) => (
                record.creator_name ? (
                    <Space size={8}>
                        <UserAvatar
                            name={record.creator_name}
                            src={record.creator_avatar}
                            size={24}
                        />
                        <span>{record.creator_name}</span>
                    </Space>
                ) : '-'
            ),
            sorter: (a, b) => (a.creator_name || '').localeCompare(b.creator_name || '')
        },

        {
            title: '',
            key: 'action',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => history.push(`/groups/${record.id}`)}>Open</Button>
                    <Dropdown 
                        menu={{
                            items: [
                                {
                                    key: 'send',
                                    label: 'Send Message',
                                    icon: <MessageOutlined />,
                                    disabled: !Number(record.contact_count),
                                    onClick: () => history.push('/campaign/create', { groupId: record.id })
                                },
                                { 
                                    key: 'add', 
                                    label: 'Add Contacts', 
                                    icon: <UserOutlined />,
                                    onClick: () => handleAddContactsTrigger(record)
                                },
                                { 
                                    key: 'export', 
                                    label: 'Export',
                                    icon: <DownloadOutlined />,
                                    onClick: () => handleExportClick(record)
                                },
                                { 
                                    key: 'duplicate', 
                                    label: 'Duplicate',
                                    icon: <CopyOutlined />,
                                    onClick: () => handleDuplicateClick(record)
                                },
                                { 
                                    key: 'delete', 
                                    label: 'Delete', 
                                    danger: true,
                                    icon: <DeleteOutlined />,
                                    onClick: () => handleDeleteClick(record)
                                },
                            ].filter(Boolean)
                        }} 
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            )
        }
    ];

    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [duplicateData, setDuplicateData] = useState({ name: '', privacy: 'public' });
    const [duplicating, setDuplicating] = useState(false);
    
    // ... duplicate logic

    const handleDuplicateClick = (group) => {
        setSelectedGroup(group);
        setDuplicateData({
            name: `Copy of ${group.name}`,
            privacy: 'public' // Default to public as requested
        });
        setDuplicateModalOpen(true);
    };

    const handleExportClick = async (group) => {
        const hide = message.loading('Exporting group contacts...', 0);
        try {
            await exportGroup(group);
            hide();
            message.success('Export finished. Downloading...');
        } catch (error) {
            hide();
            // Error handled in store
        }
    };

    const handleDuplicateSubmit = async () => {
        if (!duplicateData.name.trim()) return;
        
        setDuplicating(true);
        try {
            await duplicateGroup(selectedGroup.id, duplicateData.name, duplicateData.privacy);
            setDuplicateModalOpen(false);
            setDuplicateData({ name: '', privacy: 'public' });
            setSelectedGroup(null);
        } catch (error) {
            // Error handled in store
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <div>
            <div className="gh-compact-toolbar" style={{ 
                display: 'flex', 
                gap: 16 
            }}>
                <Input 
                    placeholder="Search groups or tags..." 
                    prefix={<SearchOutlined />} 
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ maxWidth: 300 }}
                    disabled={loading}
                />
                {/* <Select 
                    value={filters.purpose} 
                    style={{ width: 150 }} 
                    onChange={val => setFilter('purpose', val)}
                    options={[
                        { value: 'all', label: 'All Purposes' },
                        { value: 'Job Fair', label: 'Job Fair' },
                        { value: 'Candidates', label: 'Candidates' },
                        { value: 'Custom Group', label: 'Custom Group' },
                    ]}
                /> */}
                 <Select 
                    value={filters.status} 
                    style={{ width: 120 }} 
                    onChange={val => setFilter('status', val)}
                    options={[
                        { value: 'active', label: 'Active' },
                        { value: 'archived', label: 'Archived' },
                    ]}
                    disabled={loading}
                />
            </div>
            
            <div className="gh-compact-table-wrap">
                <Table 
                    className="gh-themed-table"
                    columns={columns} 
                    dataSource={data} 
                    loading={loading}
                    pagination={{ pageSize: 12 }}
                    size="small"
                />
            </div>

            <Modal
                title="Duplicate Group"
                open={duplicateModalOpen}
                onCancel={() => setDuplicateModalOpen(false)}
                onOk={handleDuplicateSubmit}
                confirmLoading={duplicating}
                okText="Duplicate"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <label style={{ display: 'network', marginBottom: 4, fontWeight: 500 }}>New Group Name</label>
                        <Input 
                            value={duplicateData.name} 
                            onChange={e => setDuplicateData({ ...duplicateData, name: e.target.value })} 
                            placeholder="Enter new group name"
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Privacy</label>
                        <Select
                            value={duplicateData.privacy}
                            onChange={val => setDuplicateData({ ...duplicateData, privacy: val })}
                            style={{ width: '100%' }}
                            options={[
                                { value: 'public', label: 'Public (Visible to everyone in company)' },
                                { value: 'private', label: 'Private (Visible only to you)' }
                            ]}
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                title="Delete or Archive Group"
                open={deleteModalOpen}
                onCancel={() => setDeleteModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setDeleteModalOpen(false)}>
                        Cancel
                    </Button>,
                    !selectedGroup?.is_archived && (
                        <Button 
                            key="archive" 
                            onClick={handleArchive}
                        >
                            Archive
                        </Button>
                    ),
                    <Button 
                        key="delete" 
                        danger 
                        type="primary" 
                        onClick={handleDelete}
                    >
                        Delete
                    </Button>
                ].filter(Boolean)}
            >
                <p>What would you like to do with <strong>{selectedGroup?.name}</strong>?</p>
                {!selectedGroup?.is_archived && (
                    <p><strong>Archive:</strong> Hides the group but keeps data intact. Can be restored later.</p>
                )}
                <p><strong>Delete:</strong> Permanently removes the group. This cannot be undone.</p>
                <p style={{ marginTop: 8, color: '#888' }}>Contacts in this group will not be affected.</p>
            </Modal>

            <AddContactsModal
                visible={addContactsModalOpen}
                onCancel={() => setAddContactsModalOpen(false)}
                onAdd={handleAddContactsToGroup}
                confirmLoading={addingContacts}
            />
        </div>
    );
};

export default withRouter(GroupList);
